/**
 * Página de test que usa datos mock en lugar de la API real.
 * Útil para desarrollar y probar ProductReel/ReelsToBuy sin depender del backend.
 *
 * Ruta: /reel/test-mock/video-personalizado
 *       /reel/test-mock/pack-fotos-premium
 *
 * O visita cualquier slug conocido del mock (video-personalizado, pack-fotos-premium)
 * con ?mock=1 en la URL si se implementa el switch en ProductReel.
 */

import { useParams, useNavigate } from 'react-router-dom';
import ReelsToBuy, { MediaItem } from '@/components/ReelsToBuy';
import { getMockProductBySlug } from '@/mocks/productBySlug';

const MOCK_SLUGS = ['video-personalizado', 'pack-fotos-premium'] as const;

export default function ProductReelMockTest() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Usar slug del mock o default
  const productSlug = slug && MOCK_SLUGS.includes(slug as any) ? slug : 'video-personalizado';
  const country = 'AR'; // Simular Argentina para precios en ARS

  const apiResponse = getMockProductBySlug(productSlug, country);

  if (!apiResponse) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white gap-4 p-4">
        <h1 className="text-xl font-bold">Mock no encontrado</h1>
        <p className="text-gray-400">
          Slugs disponibles: {MOCK_SLUGS.join(', ')}
        </p>
        <button
          onClick={() => navigate('/reel/test-mock/video-personalizado')}
          className="px-4 py-2 bg-pink-500 rounded-lg"
        >
          Probar video-personalizado
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 text-gray-400 hover:text-white"
        >
          Volver
        </button>
      </div>
    );
  }

  const { product } = apiResponse;

  // Crear items para ReelsToBuy (misma lógica que ProductReel)
  const reelItems: MediaItem[] = [];

  if (product.video_url) {
    reelItems.push({
      type: 'video',
      src: product.video_url,
      thumbnail: product.thumbnail_url || product.image_url,
    });
  }

  if (product.image_url) {
    reelItems.push({
      type: 'image',
      src: product.image_url,
      alt: product.image_alt || product.name,
    });
  }

  if (product.gallery_images?.length) {
    product.gallery_images.forEach((url) => {
      reelItems.push({
        type: 'image',
        src: url,
        alt: product.image_alt || product.name,
      });
    });
  }

  if (reelItems.length === 0) {
    reelItems.push({
      type: 'image',
      src: 'https://via.placeholder.com/400x600?text=' + encodeURIComponent(product.name),
      alt: product.name,
    });
  }

  const handlePurchase = () => {
    alert(`[MOCK] Compra simulada para ${product.name}\nPrecio: ${product.recommended_currency} ${product.recommended_price}`);
  };

  return (
    <div className="relative">
      {/* Banner indicando que es mock */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 text-black text-center py-1 text-sm font-medium">
        🧪 Modo TEST - Usando datos mock
      </div>
      <div className="pt-8">
        <ReelsToBuy
          items={reelItems}
          maxFreeItems={3}
          price={`${product.recommended_currency} ${product.recommended_price}`}
          packName={product.name}
          buttonText={product.cta_button_text || 'Comprar Ahora'}
          buttonColor={product.cta_button_color || '#ec4899'}
          heroImage={product.hero_image_url}
          description={product.page_description || product.description}
          onPurchaseClick={handlePurchase}
        />
      </div>
    </div>
  );
}
